import { MAX_OCCURRENCES_PER_QUERY } from "@dream/config/constants";
import { DateTime } from "luxon";
import { RRule, rrulestr } from "rrule";
import { validationError } from "../errors.ts";
import { assertValidTimezone } from "../time/timezone.ts";
import type { Instant } from "../types.ts";

/**
 * Expansão de recorrência RFC 5545 com segurança de timezone.
 *
 * A biblioteca `rrule` opera em UTC e não conhece fusos. Expandir instantes
 * reais direto nela produz erro de uma hora sempre que a série cruza uma
 * transição de horário de verão: "toda terça às 09:00" viraria 08:00 ou 10:00
 * depois da virada.
 *
 * Solução padrão, aplicada aqui: expandir em espaço "flutuante". O wall clock
 * local é projetado em uma data cujos componentes UTC são iguais aos
 * componentes locais; a regra é expandida nesse espaço sem fuso; e cada
 * resultado é reinterpretado no fuso da regra. Assim "09:00" permanece 09:00
 * na parede em todas as ocorrências, que é o que o usuário quis dizer.
 */

/** Instante real → data flutuante (componentes UTC == componentes locais). */
export function instantToFloating(instant: Instant, timezone: string): Date {
  const z = DateTime.fromJSDate(instant, {
    zone: assertValidTimezone(timezone),
  });
  return new Date(Date.UTC(z.year, z.month - 1, z.day, z.hour, z.minute, z.second, z.millisecond));
}

/** Data flutuante → instante real no fuso informado. */
export function floatingToInstant(floating: Date, timezone: string): Instant {
  const dt = DateTime.fromObject(
    {
      year: floating.getUTCFullYear(),
      month: floating.getUTCMonth() + 1,
      day: floating.getUTCDate(),
      hour: floating.getUTCHours(),
      minute: floating.getUTCMinutes(),
      second: floating.getUTCSeconds(),
      millisecond: floating.getUTCMilliseconds(),
    },
    { zone: assertValidTimezone(timezone) },
  );
  return dt.toJSDate();
}

/** Normaliza a string para o formato que `rrulestr` aceita. */
function normalizeRruleString(rrule: string): string {
  const trimmed = rrule.trim();
  if (trimmed === "") throw validationError("Regra de recorrência vazia");
  const upper = trimmed.toUpperCase();
  if (upper.startsWith("RRULE:") || upper.startsWith("DTSTART")) return trimmed;
  return `RRULE:${trimmed}`;
}

export interface ParsedRecurrence {
  frequency: "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";
  interval: number;
  count: number | null;
  until: Instant | null;
  byWeekday: number[];
  byMonthDay: number[];
}

/**
 * Valida a RRULE e devolve sua forma estruturada.
 * Lança `VALIDATION` para regras malformadas ou de frequência não suportada.
 */
export function parseRecurrence(
  rrule: string,
  timezone: string,
  dtstart: Instant,
): ParsedRecurrence {
  let parsed: RRule;
  try {
    const options = RRule.parseString(normalizeRruleString(rrule));
    options.dtstart = instantToFloating(dtstart, timezone);
    parsed = new RRule(options);
  } catch (error) {
    throw validationError(
      `Regra de recorrência inválida: ${error instanceof Error ? error.message : "erro desconhecido"}`,
      { rrule },
    );
  }

  const freqMap: Record<number, ParsedRecurrence["frequency"]> = {
    [RRule.DAILY]: "DAILY",
    [RRule.WEEKLY]: "WEEKLY",
    [RRule.MONTHLY]: "MONTHLY",
    [RRule.YEARLY]: "YEARLY",
  };

  const frequency = freqMap[parsed.options.freq];
  if (frequency === undefined) {
    throw validationError(
      "Frequência de recorrência não suportada. Use DAILY, WEEKLY, MONTHLY ou YEARLY.",
      { rrule },
    );
  }

  const interval = parsed.options.interval ?? 1;
  if (interval < 1 || interval > 999) {
    throw validationError("INTERVAL da recorrência deve estar entre 1 e 999", {
      interval,
    });
  }

  return {
    frequency,
    interval,
    count: parsed.options.count ?? null,
    until: parsed.options.until ? floatingToInstant(parsed.options.until, timezone) : null,
    byWeekday: parsed.options.byweekday ?? [],
    byMonthDay: parsed.options.bymonthday ?? [],
  };
}

export interface ExpandOptions {
  rrule: string;
  timezone: string;
  dtstart: Instant;
  /** Início da janela consultada, inclusivo. */
  rangeStart: Instant;
  /** Fim da janela consultada, exclusivo. */
  rangeEnd: Instant;
  /** Duração de cada ocorrência, para incluir eventos que começam antes da janela. */
  durationMs?: number;
  limit?: number;
}

/**
 * Instantes de início das ocorrências que intersectam a janela.
 *
 * A janela de busca é recuada pela duração do evento, para que um evento de
 * múltiplos dias iniciado antes de `rangeStart` mas ainda em curso apareça.
 */
export function expandRruleInRange(options: ExpandOptions): Instant[] {
  const {
    rrule,
    timezone,
    dtstart,
    rangeStart,
    rangeEnd,
    durationMs = 0,
    limit = MAX_OCCURRENCES_PER_QUERY,
  } = options;

  assertValidTimezone(timezone);

  const parsedOptions = RRule.parseString(normalizeRruleString(rrule));
  parsedOptions.dtstart = instantToFloating(dtstart, timezone);

  let rule: RRule;
  try {
    rule = new RRule(parsedOptions);
  } catch (error) {
    throw validationError(
      `Regra de recorrência inválida: ${error instanceof Error ? error.message : "erro desconhecido"}`,
      { rrule },
    );
  }

  const searchStart = new Date(rangeStart.getTime() - Math.max(0, durationMs));
  const floatingStart = instantToFloating(searchStart, timezone);
  const floatingEnd = instantToFloating(rangeEnd, timezone);

  // `inc: true` no início para não perder uma ocorrência exatamente na borda;
  // o fim permanece exclusivo, coerente com o intervalo semiaberto do produto.
  const floatingResults = rule.between(floatingStart, floatingEnd, true);

  const results: Instant[] = [];
  for (const floating of floatingResults) {
    if (results.length >= limit) break;
    if (floating.getTime() >= floatingEnd.getTime()) continue;
    results.push(floatingToInstant(floating, timezone));
  }
  return results;
}

/** Próximas `count` ocorrências a partir de um instante. Usado pelo worker. */
export function nextOccurrences(options: {
  rrule: string;
  timezone: string;
  dtstart: Instant;
  after: Instant;
  count: number;
}): Instant[] {
  const { rrule, timezone, dtstart, after, count } = options;
  const parsedOptions = RRule.parseString(normalizeRruleString(rrule));
  parsedOptions.dtstart = instantToFloating(dtstart, timezone);
  const rule = new RRule(parsedOptions);
  const floatingAfter = instantToFloating(after, timezone);

  const out: Instant[] = [];
  let cursor: Date | null = rule.after(floatingAfter, false);
  while (cursor !== null && out.length < count) {
    out.push(floatingToInstant(cursor, timezone));
    cursor = rule.after(cursor, false);
  }
  return out;
}

/** Descrição legível em pt-BR, para a UI e para o histórico. */
export function describeRecurrence(rrule: string, timezone: string, dtstart: Instant): string {
  const parsed = parseRecurrence(rrule, timezone, dtstart);
  const { frequency, interval } = parsed;

  const unit = {
    DAILY: "dia",
    WEEKLY: "semana",
    MONTHLY: "mês",
    YEARLY: "ano",
  }[frequency];
  const unitPlural = {
    DAILY: "dias",
    WEEKLY: "semanas",
    MONTHLY: "meses",
    YEARLY: "anos",
  }[frequency];

  let base = interval === 1 ? `A cada ${unit}` : `A cada ${interval} ${unitPlural}`;

  // Para FREQ=WEEKLY a biblioteca infere BYDAY a partir do DTSTART. Listar um
  // dia que a regra não declarou daria a impressão de uma restrição que não
  // existe, então só descrevemos os dias quando o BYDAY é explícito.
  const hasExplicitByDay = /BYDAY=/i.test(rrule);

  if (frequency === "WEEKLY" && hasExplicitByDay && parsed.byWeekday.length > 0) {
    const names = ["segunda", "terça", "quarta", "quinta", "sexta", "sábado", "domingo"];
    const days = parsed.byWeekday
      .map((d) => names[d] ?? String(d))
      .filter((d): d is string => d !== undefined);
    if (days.length > 0) base += ` (${days.join(", ")})`;
  }

  if (frequency === "MONTHLY" && parsed.byMonthDay.length > 0) {
    base += ` no dia ${parsed.byMonthDay.join(", ")}`;
  }

  if (parsed.count !== null) {
    base += `, ${parsed.count} ${parsed.count === 1 ? "vez" : "vezes"}`;
  } else if (parsed.until !== null) {
    const untilLabel = DateTime.fromJSDate(parsed.until, {
      zone: timezone,
    }).toFormat("dd/MM/yyyy");
    base += `, até ${untilLabel}`;
  }

  return base;
}

/** Serializa EXDATEs para persistência textual em iCalendar. */
export function serializeExceptions(exceptions: Instant[], timezone: string): string {
  if (exceptions.length === 0) return "";
  const stamps = exceptions
    .map((e) => DateTime.fromJSDate(e, { zone: timezone }).toFormat("yyyyMMdd'T'HHmmss"))
    .join(",");
  return `EXDATE;TZID=${timezone}:${stamps}`;
}

export { rrulestr };
